import { FhirBundleSdk } from "@metriport/fhir-sdk";
import { ISO_DATE } from "@metriport/shared/common/date";
import dayjs from "dayjs";
import {
  getObservationCode as getObservationCodeForGrouping,
  getObservationDate,
  groupResourcesByKey,
  ObservationSeries,
} from "../../table-transforms/series-utils";
import { getObservationValue } from "../../utils/observation";
import { createBloodPressureRow, partitionBloodPressureSeries } from "./blood-pressure";
import { convertAndFormatVital, detectVitalType, UnitInfo, UnitSystem } from "./unit-conversions";

export type ChartSeries = {
  name: string;
  data: Array<{ date: string; value: number }>;
  color?: string;
};

export type VitalRowData = {
  id: string;
  observation: string;
  mostRecentValue: string;
  mostRecentDate: string;
  originalData: ObservationSeries;
  chartSeries: ChartSeries[];
  originalValue?: number;
  originalUnit?: string;
  unitInfo: UnitInfo;
  displayValue: (unitSystem: UnitSystem) => string;
};

function extractObservationData(series: ObservationSeries): Array<{ date: string; value: number }> {
  const data: Array<{ date: string; value: number }> = [];

  for (const resource of series.resources) {
    const date = getObservationDate(resource);
    if (!date || date === "-") continue;

    const value = resource.valueQuantity?.value;
    if (value === undefined) continue;

    data.push({ date, value });
  }

  return data;
}

function createVitalRow(series: ObservationSeries): VitalRowData {
  const latestObservation = series.resources[series.resources.length - 1];

  if (!latestObservation) {
    throw new Error("Series should always have at least one resource");
  }

  const observationName =
    latestObservation.code?.coding?.[0]?.display || latestObservation.code?.text || "-";

  const originalValue = latestObservation.valueQuantity?.value;
  const originalUnit = latestObservation.valueQuantity?.unit;
  const unitInfo = detectVitalType(originalUnit);

  const defaultDisplayValue = getObservationValue(latestObservation);

  const displayValue = (unitSystem: UnitSystem): string => {
    if (originalValue === undefined) {
      return defaultDisplayValue;
    }

    if (unitInfo.vitalType === "other") {
      return defaultDisplayValue;
    }

    return convertAndFormatVital(originalValue, unitInfo, unitSystem);
  };

  return {
    id: `series-${series.code}`,
    observation: observationName,
    mostRecentValue: defaultDisplayValue,
    mostRecentDate: formatDate(series.latestDate),
    originalData: series,
    chartSeries: [
      {
        name: observationName,
        data: extractObservationData(series),
      },
    ],
    originalValue,
    originalUnit,
    unitInfo,
    displayValue,
  };
}

export function getVitalRows({ bundle }: { bundle: FhirBundleSdk }): VitalRowData[] {
  const observations = bundle.getObservations().filter(observation => {
    return (
      observation.category?.some(cat =>
        cat.coding?.some(coding => coding.code?.toLowerCase() === "vital-signs")
      ) ?? false
    );
  });

  const observationSeries = groupResourcesByKey(
    observations,
    getObservationCodeForGrouping,
    getObservationDate,
    "Observation"
  );

  const { systolic, diastolic, other } = partitionBloodPressureSeries(observationSeries);

  const bpRows =
    systolic && diastolic
      ? [createBloodPressureRow(systolic, diastolic)]
      : [systolic, diastolic].filter((s): s is ObservationSeries => Boolean(s)).map(createVitalRow);

  return [...bpRows, ...other.map(createVitalRow)];
}

function formatDate(date: string | undefined): string {
  if (!date || date === "-") return "-";

  const dayjsDate = dayjs(date);
  if (!dayjsDate.isValid()) return "-";

  return dayjsDate.format(ISO_DATE);
}
